import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton, NioIcon } from '../../../components';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

function RequestDemandStep9() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t } = useTranslation();

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestDetails = location.state?.requestDetails || {};
  const requestStep4Details = location.state?.requestStep4Details || {};
  const requestStep5Details = location.state?.requestStep5Details || {};
  const requestStep6Details = location.state?.requestStep6Details || {};
  const requestStep7Details = location.state?.requestStep7Details || {};
  const requestStep8Details = location.state?.requestStep8Details || {};
  const isStep5Skipped = location.state?.isStep5Skipped || false;
  const displayStep = isStep5Skipped ? 8 : 9;

  const [budget, setBudget] = useState('');
  const [customBudget, setCustomBudget] = useState('');
  const [providers, setProviders] = useState([]);
  const [reviewStatsByProvider, setReviewStatsByProvider] = useState({});
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [providersError, setProvidersError] = useState('');
  const budgetRanges = ['10-100', '250-500', '500-1000', '1000-5000', '10000-20000', '+50000'];

  const userCity = requestStep7Details.city || '';
  const userRegion = requestStep7Details.region || '';

  const normalizeText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const tokenize = (value) =>
    normalizeText(value)
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length >= 3);

  const getProviderHaystack = (provider) =>
    [
      provider?.serviceCategory,
      provider?.subSector,
      provider?.sector,
      provider?.description,
      provider?.qualifications,
      provider?.businessName,
      provider?.about,
      provider?.servicesOffered,
      provider?.detailedSubServices,
      ...(Array.isArray(provider?.skills) ? provider.skills : []),
      ...(Array.isArray(provider?.serviceCategories)
        ? provider.serviceCategories.map((entry) => entry?.serviceCategory)
        : []),
    ]
      .filter(Boolean)
      .map((entry) => normalizeText(entry))
      .join(' ');

  const scoreProviderSimilarity = (provider) => {
    let score = 0;
    const subNorm = normalizeText(selectedSubService);
    const parentNorm = normalizeText(parentServiceTitle);
    const haystack = getProviderHaystack(provider);
    const subTokens = tokenize(selectedSubService);

    if (normalizeText(provider?.serviceCategory) === subNorm) score += 120;
    if (normalizeText(provider?.subSector) === subNorm) score += 110;

    subTokens.forEach((token) => {
      if (haystack.includes(token)) score += 18;
    });

    if (parentNorm) {
      if (normalizeText(provider?.sector) === parentNorm) score += 45;
      if (haystack.includes(parentNorm)) score += 25;
    }

    const cityNorm = normalizeText(userCity);
    const regionNorm = normalizeText(userRegion);
    if (cityNorm && normalizeText(provider?.city) === cityNorm) score += 30;
    if (regionNorm && normalizeText(provider?.region) === regionNorm) score += 20;
    if (cityNorm && haystack.includes(cityNorm)) score += 10;

    return score;
  };

  useEffect(() => {
    const loadProviders = async () => {
      if (!serviceId) {
        setLoadingProviders(false);
        setProviders([]);
        return;
      }
      try {
        setLoadingProviders(true);
        setProvidersError('');
        const response = await api.getProvidersByService(decodeURIComponent(serviceId));
        const list = response?.success && Array.isArray(response.data) ? response.data : [];
        setProviders(list);
      } catch (error) {
        setProviders([]);
        setProvidersError(error.message || t('wizard.step9ProvidersError'));
      } finally {
        setLoadingProviders(false);
      }
    };
    loadProviders();
  }, [serviceId]);

  useEffect(() => {
    const loadReviewStats = async () => {
      const ids = providers.map((p) => p._id).filter(Boolean);
      if (!ids.length) {
        setReviewStatsByProvider({});
        return;
      }
      try {
        const res = await api.getProviderReviewsBatch(ids);
        if (res?.success) {
          setReviewStatsByProvider(res.statsByProvider || {});
        }
      } catch {
        setReviewStatsByProvider({});
      }
    };
    loadReviewStats();
  }, [providers]);

  const similarProviders = useMemo(() => {
    if (!providers.length) return [];

    const scored = providers
      .map((provider) => ({ provider, score: scoreProviderSimilarity(provider) }))
      .sort((a, b) => b.score - a.score);

    const strongMatches = scored.filter((entry) => entry.score >= 18);
    const source = strongMatches.length ? strongMatches : scored;

    return source.slice(0, 6).map((entry) => entry.provider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers, selectedSubService, parentServiceTitle, userCity, userRegion]);

  const averagePrice = useMemo(() => {
    const prices = similarProviders
      .map((provider) => Number(String(provider?.pricing || '').replace(/[^\d.]/g, '')))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!prices.length) return null;
    const avg = Math.round(prices.reduce((acc, value) => acc + value, 0) / prices.length);
    return `${avg} TND`;
  }, [similarProviders]);

  return (
    <AppLayout title={`${t('wizard.step', { n: displayStep })} - ${t('wizard.step9Title')}`} rootClass="layout-1">
      <style>{`
        .request-step9-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step9-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step9-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step9-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 2rem;
        }
        .request-step9-label {
          color: #0f2b3d;
          font-size: 1.05rem;
          font-weight: 500;
          margin-bottom: 0.7rem;
        }
        .request-step9-budget-wrap {
          position: relative;
          margin-bottom: 0.8rem;
        }
        .request-step9-budget-input {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          height: 44px;
          padding: 0 2rem 0 0.9rem;
          color: #0f2b3d;
          background: #fff;
        }
        .request-step9-currency {
          position: absolute;
          right: 0.7rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6f7d8c;
          font-weight: 600;
        }
        .request-step9-average {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          background: #eaf1ff;
          color: #2563eb;
          border-radius: 8px;
          min-height: 34px;
          padding: 0.3rem 0.55rem;
          margin-bottom: 2.4rem;
          font-size: 0.9rem;
          font-weight: 500;
        }
        .request-step9-average strong {
          color: #1d4ed8;
        }
        .request-step9-similar-title {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.35rem;
        }
        .request-step9-similar-desc {
          color: #5f6b7a;
          margin-bottom: 1rem;
        }
        .request-step9-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.75rem;
        }
        .request-step9-card {
          border: 1px solid #d6dde5;
          border-radius: 10px;
          background: #fff;
          padding: 0.9rem;
          min-height: 210px;
          display: flex;
          flex-direction: column;
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .request-step9-card:hover {
          box-shadow: 0 8px 22px rgba(15, 43, 61, 0.1);
          transform: translateY(-1px);
        }
        .request-step9-card-head {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          margin-bottom: 0.65rem;
        }
        .request-step9-card-avatar {
          width: 44px;
          height: 44px;
          border-radius: 999px;
          object-fit: cover;
          flex-shrink: 0;
          background: #2563eb;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
        }
        .request-step9-card-top {
          display: flex;
          justify-content: space-between;
          gap: 0.5rem;
          margin-bottom: 0.55rem;
          flex: 1;
        }
        .request-step9-card-name {
          color: #0f2b3d;
          font-weight: 700;
          font-size: 0.95rem;
        }
        .request-step9-card-price {
          background: #2563eb;
          color: #fff;
          border-radius: 6px;
          padding: 0.14rem 0.35rem;
          font-size: 0.72rem;
          font-weight: 600;
          height: fit-content;
          white-space: nowrap;
        }
        .request-step9-card-line {
          color: #0f2b3d;
          font-size: 0.88rem;
          margin-bottom: 0.28rem;
        }
        .request-step9-card-location {
          color: #6f7d8c;
          font-size: 0.84rem;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          margin-top: auto;
          padding-top: 0.6rem;
        }
        .request-step9-card-rating {
          color: #f59e0b;
          font-size: 0.82rem;
          font-weight: 600;
          margin-top: 0.15rem;
        }
        .request-step9-card-link {
          margin-top: 0.65rem;
          font-size: 0.84rem;
          font-weight: 600;
          color: #2563eb;
          text-decoration: none;
        }
        .request-step9-card-link:hover {
          text-decoration: underline;
        }
        .request-step9-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
        @media (max-width: 992px) {
          .request-step9-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 575px) {
          .request-step9-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step9-page p-3 p-md-4">
            <div className="request-step9-content">
              <div className="request-step9-step">{t('wizard.step', { n: displayStep })}</div>
              <h2 className="request-step9-heading">{t('wizard.step9Heading')}</h2>

              <h3 className="request-step9-label">{t('wizard.step9BudgetQuestion')}</h3>
              <div className="request-step9-budget-wrap">
                <select
                  className="request-step9-budget-input"
                  value={budget}
                  onChange={(event) => setBudget(event.target.value)}
                >
                  <option value="">{t('wizard.step9SelectRange')}</option>
                  {budgetRanges.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
                <span className="request-step9-currency">TND</span>
              </div>
              <div className="request-step9-budget-wrap">
                <input
                  className="request-step9-budget-input"
                  type="number"
                  min="0"
                  placeholder={t('wizard.step9CustomBudget')}
                  value={customBudget}
                  onChange={(event) => setCustomBudget(event.target.value)}
                />
                <span className="request-step9-currency">TND</span>
              </div>

              <div className="request-step9-average">
                <NioIcon name="bar-chart-line" />
                <span>
                  {t('wizard.step9Average')}{' '}
                  <strong>{averagePrice || 'N/A'}</strong>
                </span>
              </div>

              <h3 className="request-step9-similar-title">{t('wizard.step9SimilarTitle')}</h3>
              <p className="request-step9-similar-desc">
                {t('wizard.step9SimilarDesc', {
                  subService: translateServiceLabel(t, selectedSubService),
                  parent: translateServiceLabel(t, parentServiceTitle),
                  zone: userCity || userRegion
                    ? t('wizard.step9SimilarZone', { zone: [userCity, userRegion].filter(Boolean).join(', ') })
                    : '',
                })}
              </p>

              {loadingProviders ? (
                <p>{t('wizard.step9LoadingProviders')}</p>
              ) : providersError ? (
                <p>{providersError}</p>
              ) : similarProviders.length === 0 ? (
                <p>
                  {t('wizard.step9NoProviders')}
                  {' '}
                  <Link to={`/service/${serviceId}/providers`}>{t('wizard.step9ViewAllProviders')}</Link>
                </p>
              ) : (
                <div className="request-step9-grid">
                  {similarProviders.map((provider, index) => {
                    const providerName =
                      [provider?.name, provider?.lastName].filter(Boolean).join(' ') ||
                      provider?.businessName ||
                      t('wizard.step9ProviderFallback');
                    const providerPricing = provider?.pricing || t('wizard.step9OnQuote');
                    const zone =
                      [provider?.city, provider?.region].filter(Boolean).join(', ') ||
                      provider?.coverageGuarantees ||
                      provider?.location ||
                      t('wizard.step9ZoneUnknown');
                    const stats = reviewStatsByProvider[String(provider?._id)];
                    const initials = providerName.charAt(0).toUpperCase();
                    const specialty = translateServiceLabel(
                      t,
                      provider?.serviceCategory || provider?.subSector || selectedSubService,
                    );

                    return (
                      <div key={provider?._id || index} className="request-step9-card">
                        <div className="request-step9-card-head">
                          {provider?.photo ? (
                            <img
                              src={provider.photo}
                              alt={providerName}
                              className="request-step9-card-avatar"
                            />
                          ) : (
                            <span className="request-step9-card-avatar">{initials}</span>
                          )}
                          <div className="flex-grow-1">
                            <div className="request-step9-card-name">{providerName}</div>
                            {provider?.businessName ? (
                              <div className="text-muted small">{provider.businessName}</div>
                            ) : null}
                            {stats?.count ? (
                              <div className="request-step9-card-rating">
                                <NioIcon name="star-fill" className="me-1" /> {stats.averageRating} ({t('wizard.step9ReviewsCount', { count: stats.count })})
                              </div>
                            ) : null}
                          </div>
                          <div className="request-step9-card-price">{providerPricing}</div>
                        </div>
                        <div className="request-step9-card-line">
                          <strong>{specialty}</strong>
                        </div>
                        <div className="request-step9-card-line">
                          {provider?.description
                            ? String(provider.description).slice(0, 110) + (provider.description.length > 110 ? '…' : '')
                            : t('wizard.step9InterventionFor', {
                                subService: translateServiceLabel(t, selectedSubService).toLowerCase(),
                              })}
                        </div>
                        <div className="request-step9-card-line">
                          {provider?.experience
                            ? t('wizard.step9YearsExperience', { years: provider.experience })
                            : provider?.qualifications || t('wizard.step9VerifiedProvider')}
                        </div>
                        <div className="request-step9-card-location">
                          <NioIcon name="geo-alt" />
                          <span>{zone}</span>
                        </div>
                        <Link
                          to={`/providers/${provider._id}`}
                          state={{
                            provider,
                            fromPath: `/service/${serviceId}/request-demand-step9`,
                            serviceId,
                            serviceTitle: parentServiceTitle,
                          }}
                          className="request-step9-card-link"
                        >
                          {t('wizard.step9ViewProfile')}
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="request-step9-footer">
              <NioButton
                className="btn btn-primary"
                label={t('common.back')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className={`btn ${
                  budget.trim() || customBudget.trim()
                    ? 'btn-primary'
                    : 'btn-primary disabled'
                }`}
                label={t('common.next')}
                onClick={() => {
                  if (!budget.trim() && !customBudget.trim()) return;
                  navigate(`/service/${serviceId}/request-demand-step10`, {
                    state: {
                      selectedSubService,
                      parentServiceTitle,
                      projectTitle,
                      isStep5Skipped,
                      requestDetails,
                      requestStep4Details,
                      requestStep5Details,
                      requestStep6Details,
                      requestStep7Details,
                      requestStep8Details,
                      requestStep9Details: {
                        budget: customBudget.trim() || budget.trim(),
                      },
                    },
                  });
                }}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandStep9;
